import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";
import {
    canPayRequest,
    createScanWorkflowApi,
} from "../../scripts/api_scan_workflow";
import ScanCheckout from "./ScanCheckout";
import ScanLoading from "../../components/Scan/ScanLoading";
import Table from "../../components/Table";
import { ArrowBackRounded, ExploreOutlined, RefreshRounded } from "@mui/icons-material";
import {
    PAYMENT_LABELS,
    SCAN_LABELS,
} from "./scanRequestStatus";

const SIGNATURE_PENDING_STATUSES = [
    "sending",
    "created",
    "sent",
    "pending",
    "awaiting_signature",
];

const SCAN_ACTIVE_STATUSES = [
    "pending_creation",
    "pending",
    "queued",
    "running",
    "launching",
    "processing",
    "in_progress",
];

const getTargetLabel = value => {
    if (!value) {
        return "—";
    }

    try {
        return new URL(value).hostname;
    } catch {
        return value;
    }
};

const getProgression = request => {
    const scanStatus = request.scan_status ?? request.status;
    const signaturePending = SIGNATURE_PENDING_STATUSES.includes(
        request.signature_status
    );
    const paymentPending = request.payment_status !== "paid";

    const reportReady =
        scanStatus === "completed"
        && request.report_available === true;

    if (signaturePending) {
        return {
            label: "En attente de signature",
            tone: "pending",
            step: 1,
        };
    }

    if (paymentPending) {
        return {
            label: "En attente de règlement",
            tone: "payment",
            step: 2,
        };
    }

    if (["failed", "launch_failed", "launch_unknown", "blocked"].includes(scanStatus)) {
        return {
            label: scanStatus === "launch_unknown" ? "Lancement à vérifier"
                : scanStatus === "blocked" ? "Vérification nécessaire" : "Scan en erreur",
            tone: "error",
            step: 3,
        };
    }

    if (reportReady) {
        return {
            label: "Scan disponible",
            tone: "complete",
            step: 3,
        };
    }

    if (SCAN_ACTIVE_STATUSES.includes(scanStatus) || scanStatus === "running" || scanStatus === "completed") {
        return {
            label: "Scan en cours",
            tone: "active",
            step: 3,
        };
    }

    return {
        label: "En attente du démarrage",
        tone: "pending",
        step: 3,
    };
};

function WorkflowProgressBadge({ progression }) {
    const stepLabels = [
        "Étape 1 : Signature du mandat",
        "Étape 2 : Paiement",
        "Étape 3 : Analyse DAST",
    ];

    return (
        <span className={`scan-workflow-progress scan-workflow-progress--${progression.tone}`}>
            <span className="scan-workflow-progress-steps">
                {[1, 2, 3].map(step => (
                    <span
                        className={`scan-workflow-step scan-workflow-tooltip ${step <= progression.step ? "is-complete" : ""}`}
                        key={step}
                        data-tooltip={stepLabels[step - 1]}
                        aria-label={stepLabels[step - 1]}
                        tabIndex="0"
                    >
                        {progression.tone === "complete" && step === 3
                            ? "✓"
                            : step < progression.step
                                ? "✓"
                                : step}
                    </span>
                ))}
            </span>
            <span> {progression.label}</span>
        </span>
    );
}

function ResendMandateButton({ request, deadline = 0, resendingId, onResend }) {
    const { t } = useTranslation();
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        setNow(Date.now());
        if (deadline <= Date.now()) return;

        const interval = window.setInterval(() => {
            const currentTime = Date.now();
            setNow(currentTime);
            if (currentTime >= deadline) window.clearInterval(interval);
        }, 1000);

        return () => window.clearInterval(interval);
    }, [deadline]);

    const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
    const tooltip = t("Veuillez patienter avant de renvoyer le mandat.");

    return (
        <span className="scan-workflow-resend">
            <button
                type="button"
                className="buttons-ecp scan-workflow-action-button"
                disabled={resendingId !== null || seconds > 0}
                onClick={() => onResend(request)}
            >
                {t(resendingId === String(request.request_id) ? "Renvoi…" : "Renvoyer le mandat")}
            </button>
            {seconds > 0 && (
                <span
                    className="scan-workflow-resend-cooldown scan-workflow-tooltip"
                    tabIndex={0}
                    data-tooltip={tooltip}
                    aria-label={tooltip}
                >
                    <ExploreOutlined fontSize="small" aria-hidden="true" />
                    <span aria-hidden="true">{seconds} s</span>
                </span>
            )}
        </span>
    );
}

export default function ScanWorkflow({
    onBack,
    onOpenProgress,
    onPaymentConfirmed,
    returnOnly = false,
}) {
    const { t, i18n } = useTranslation();

    const {
        apiUrl,
        token,
        reauthenticate,
    } = useContext(AuthContext);

    const api = useMemo(
        () => createScanWorkflowApi(apiUrl, token),
        [apiUrl, token]
    );

    const [refresh, setRefresh] = useState(0);

    const [history, setHistory] = useState({
        loading: true,
        requests: [],
        total: 0,
        page_size: 1,
    });

    const [updates, setUpdates] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [resendingId, setResendingId] = useState(null);
    const [resendDeadlines, setResendDeadlines] = useState({});
    const [checkoutNotice, setCheckoutNotice] = useState(null);
    const [resendSuccess, setResendSuccess] = useState(null);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        if (!resendSuccess) return;

        const timeout = window.setTimeout(() => setResendSuccess(null), 5000);
        return () => window.clearTimeout(timeout);
    }, [resendSuccess]);

    const startResendCooldown = (requestId, seconds) => {
        const startedAt = Date.now();
        setResendDeadlines(previous => ({
            ...previous,
            [requestId]: startedAt + seconds * 1000,
        }));
    };

    const checkoutLock = useRef(false);
    const alive = useRef(false);
    const currentApi = useRef(api);

    currentApi.current = api;

    useEffect(() => {
        alive.current = true;

        return () => {
            alive.current = false;
        };
    }, []);

    /*
     * On ne charge l'historique QUE lorsque
     * returnOnly === false.
     *
     * Lors d'un retour Stripe, seul ScanCheckout
     * doit travailler.
     */
    useEffect(() => {
        if (returnOnly) {
            return;
        }

        const controller = new AbortController();

        setHistory(previous => previous.api === api
            ? { ...previous, loading: true, error: null }
            : { api, loading: true, requests: [], total: 0, page_size: 1 }
        );

        api.list(controller.signal)
            .then(data => {
                if (!controller.signal.aborted) {
                    setUpdates({});
                    setHistory({
                        ...data,
                        api,
                        loading: false,
                    });
                }
            })
            .catch(error => {
                if (!controller.signal.aborted) {
                    setHistory(previous => ({
                        ...previous,
                        loading: false,
                        error,
                    }));
                }
            });

        return () => controller.abort();

    }, [
        api,
        refresh,
        returnOnly,
    ]);

    const updateRequest = useCallback(request => {
        setUpdates(previous => ({
            ...previous,
            [request.request_id]: request,
        }));
    }, []);

    const reload = useCallback(() => {
        setRefresh(value => value + 1);
    }, []);

    const pay = async request => {
        if (
            checkoutLock.current
            || !canPayRequest(request)
        ) {
            return;
        }

        checkoutLock.current = true;

        setBusyId(String(request.request_id));
        setCheckoutNotice(null);

        try {
            const checkout = await api.checkout(
                request.request_id
            );

            if (
                alive.current
                && currentApi.current === api
            ) {
                window.location.assign(
                    checkout.checkout_url
                );
            }

        } catch (error) {
            if (
                !alive.current
                || currentApi.current !== api
            ) {
                return;
            }

            const status = error.response?.status;

            if (status === 409) {
                try {
                    const updated = await api.get(
                        request.request_id
                    );

                    if (
                        !alive.current
                        || currentApi.current !== api
                    ) {
                        return;
                    }

                    updateRequest(updated);

                    setCheckoutNotice({
                        message:
                            "La demande a été actualisée. Consultez son état avant de poursuivre.",
                        request: updated,
                    });

                } catch {
                    setCheckoutNotice({
                        message:
                            "Le paiement n’est pas disponible. Actualisez la demande pour vérifier son état.",
                    });
                }

            } else {
                setCheckoutNotice({
                    message:
                        status === 502 || status === 503
                            ? "Le paiement est temporairement indisponible. Réessayez cette même demande."
                            : error.code === "authentication"
                                || status === 401
                                || status === 403
                                ? "Reconnectez-vous pour poursuivre le paiement."
                                : "Impossible de préparer le paiement. Vérifiez la demande avant de réessayer.",

                    request,

                    retry:
                        status === 502
                        || status === 503,

                    authentication:
                        error.code === "authentication"
                        || status === 401
                        || status === 403,
                });
            }

        } finally {
            checkoutLock.current = false;

            if (alive.current) {
                setBusyId(null);
            }
        }
    };

    const resendSignature = async request => {
        if (
            resendingId !== null
            || request.signature_status !== "sent"
            || resendDeadlines[request.request_id] > Date.now()
        ) {
            return;
        }

        setResendingId(String(request.request_id));
        setCheckoutNotice(null);
        setResendSuccess(null);

        try {
            await api.resendSignature(request.request_id);
            if (!alive.current || currentApi.current !== api) return;

            setResendDeadlines(previous => {
                const next = { ...previous };
                delete next[request.request_id];
                return next;
            });
            setResendSuccess({ requestId: request.request_id });

            // A refresh failure must not turn a successful resend into an error.
            try {
                const updated = await api.get(request.request_id);
                if (alive.current && currentApi.current === api) updateRequest(updated);
            } catch {
                // The history can still be refreshed manually.
            }
        } catch (error) {
            if (!alive.current || currentApi.current !== api) return;
            const status = error.response?.status;

            if (status === 429) {
                const data = error.response?.data;
                const messageSeconds = typeof data?.error === "string"
                    ? data.error.match(/(\d+)\s*seconde/i)?.[1]
                    : undefined;
                const seconds = Number(data?.retry_after ?? error.response?.headers?.["retry-after"] ?? messageSeconds);
                startResendCooldown(
                    request.request_id,
                    Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 600
                );
                return;
            }

            setCheckoutNotice({
                message: status === 409 && typeof error.response?.data?.error === "string"
                    ? error.response.data.error
                    : "Impossible de renvoyer le mandat. Vérifiez l’état de la demande.",
            });
            if (status === 409) {
                // The signature may have completed since the history was loaded.
                try {
                    const updated = await api.get(request.request_id);
                    if (alive.current && currentApi.current === api) updateRequest(updated);
                } catch {
                    // Keep the specific refusal visible if refreshing fails.
                }
            }
        } finally {
            if (alive.current) setResendingId(null);
        }
    };

    const visibleRequests = useMemo(
        () => history.requests.map(request => updates[request.request_id] || request),
        [history.requests, updates]
    );

    const formatDate = value => {
        const date = new Date(value);

        return value && !Number.isNaN(date.getTime())
            ? date.toLocaleString(
                i18n.language || "fr"
            )
            : "—";
    };
    const tableData = useMemo(

        () => visibleRequests.map(request => ({
            id: request.request_id,
            date: formatDate(request.created_at),
            target: request.target_url ?? request.data_scan?.url ?? "—",
            targetLabel: getTargetLabel(request.target_url ?? request.data_scan?.url),
            progression: getProgression(request),
            action: request,
        })),
        [visibleRequests, i18n.language]
    );

    const pendingRequests = visibleRequests.filter(request =>
        getProgression(request).tone !== "complete"
    ).length;

    const tableColumns = useMemo(() => [
        {
            Header: "Date",
            accessor: "date",
            headerColumn: true,
            filterable: true,
        },
        {
            Header: "Cible",
            accessor: "target",
            headerColumn: true,
            filterable: true,
            Cell: ({ cell }) => (
                <span
                    className="scan-workflow-target"
                    title={cell.row.original.target}
                >
                    {cell.row.original.targetLabel}
                </span>
            ),
        },
        {
            Header: "Progression",
            accessor: "progression.label",
            headerColumn: true,
            filterable: true,
            Cell: ({ cell }) => (
                <WorkflowProgressBadge
                    progression={cell.row.original.progression}
                />
            ),
        },
        {
            Header: "Actions",
            accessor: "action.request_id",
            headerColumn: true,
            filterable: false,
            Cell: ({ cell }) => {
                const request = cell.row.original.action;
                const progression = cell.row.original.progression;

                return (
                    <div className="scan-workflow-actions">
                        {request.signature_status === "sent" && (
                            <ResendMandateButton
                                request={request}
                                deadline={resendDeadlines[request.request_id]}
                                resendingId={resendingId}
                                onResend={resendSignature}
                            />
                        )}

                        {canPayRequest(request) && (
                            <button
                                type="button"
                                className="buttons-ecp buttons-ecp-create"
                                disabled={busyId !== null}
                                onClick={() => pay(request)}
                            >
                                {t(
                                    busyId === String(request.request_id)
                                        ? "Préparation…"
                                        : "Payer"
                                )}
                            </button>
                        )}

                        {progression.step === 3 && request.request_id && (
                            <button
                                type="button"
                                className="buttons-ecp scan-workflow-action-button"
                                onClick={() => onOpenProgress(request)}
                            >
                                {t("Voir la progression")}
                            </button>
                        )}

                    </div>
                );
            },
        },
    ], [
        busyId,
        onOpenProgress,
        resendSignature,
        resendingId,
        resendDeadlines,
        t,
    ]);

    return (
        <>
            {/*
             * Toujours monté.
             *
             * Sans paramètres Stripe/DocuSign,
             * ScanCheckout retourne simplement null.
             */}
            <ScanCheckout
                api={api}
                onPay={pay}
                onUpdated={updateRequest}
                onReload={reload}
                onResume={reload}
                onReconnect={reauthenticate}
                onPaymentConfirmed={onPaymentConfirmed}
            />

            {/*
             * Lors du retour Stripe on ne montre
             * rien d'autre.
             */}
            {!returnOnly && (
                <>
                    <div className="scan-workflow-toast-position">
                        <AnimatePresence>
                            {resendSuccess && (
                                <motion.div
                                    key="mandate-resent"
                                    className="scan-payment-accepted-toast"
                                    role="status"
                                    aria-live="polite"
                                    aria-atomic="true"
                                    initial={{ opacity: 0, y: reducedMotion ? 0 : -60 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: reducedMotion ? 0 : -24 }}
                                    transition={{ duration: reducedMotion ? 0 : 0.3, ease: "easeOut" }}
                                >
                                    <span className="scan-payment-accepted-toast-icon" aria-hidden="true">✓</span>
                                    <span>
                                        <strong>{t("Mandat renvoyé")}</strong>
                                        <small>{t("Vous pouvez vérifier votre boîte mail.")}</small>
                                    </span>
                                    <button
                                        type="button"
                                        aria-label={t("Fermer")}
                                        onClick={() => setResendSuccess(null)}
                                    >
                                        ×
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    {checkoutNotice && (
                        <div
                            className="scan-payment-return"
                            role="status"
                        >
                            <div>
                                <p>
                                    {t(
                                        checkoutNotice.message
                                    )}
                                </p>

                                {checkoutNotice.request && (
                                    <p>
                                        {t(
                                            PAYMENT_LABELS[
                                            checkoutNotice.request
                                                .payment_status
                                            ]
                                            || checkoutNotice.request
                                                .payment_status
                                            || "État en attente"
                                        )}

                                        {" · "}

                                        {t(
                                            SCAN_LABELS[
                                            checkoutNotice.request
                                                .scan_status
                                            ]
                                            || checkoutNotice.request
                                                .scan_status
                                            || ""
                                        )}
                                    </p>
                                )}
                            </div>

                            {checkoutNotice.retry
                                && canPayRequest(
                                    updates[
                                    checkoutNotice.request
                                        .request_id
                                    ]
                                    || checkoutNotice.request
                                ) && (
                                    <button
                                        type="button"
                                        className="buttons-ecp buttons-ecp-create"
                                        disabled={busyId !== null}
                                        onClick={() =>
                                            pay(
                                                updates[
                                                checkoutNotice.request
                                                    .request_id
                                                ]
                                                || checkoutNotice.request
                                            )
                                        }
                                    >
                                        {t(
                                            "Réessayer le paiement"
                                        )}
                                    </button>
                                )}

                            {checkoutNotice.authentication && (
                                <button
                                    type="button"
                                    className="buttons-ecp buttons-ecp-create"
                                    onClick={reauthenticate}
                                >
                                    {t("Se reconnecter")}
                                </button>
                            )}

                            <button
                                type="button"
                                className="buttons-ecp buttons-ecp-create"
                                onClick={reload}
                            >
                                {t(
                                    "Actualiser les demandes"
                                )}
                            </button>
                        </div>
                    )}

                    <section
                        className="scan-requests"
                        aria-label={t(
                            "Demandes de scans"
                        )}
                    >
                        <button
                            type="button"
                            className="scan-workflow-back-link"
                            onClick={onBack}
                        >
                            <ArrowBackRounded aria-hidden="true" />
                            {t("Retour aux scans")}
                        </button>

                        <div className="scan-workflow-heading">
                            <div>
                                <h2>{t("Suivi des scans")}</h2>
                                {history.loading ? (
                                    <div
                                        className="scan-workflow-summary-loading"
                                        aria-hidden="true"
                                    >
                                        <span />
                                        <span />
                                    </div>
                                ) : (
                                    <p>
                                        {history.total} {t("demandes au total")}
                                        {" · "}
                                        <span className="scan-workflow-pending-dot" />
                                        {pendingRequests} {t("en attente")}
                                    </p>
                                )}
                            </div>

                        </div>

                        {!token && (
                            <div
                                role="alert"
                                className="scan-error"
                            >
                                <p>
                                    {t(
                                        "Reconnectez-vous pour accéder aux demandes avec votre authentification backend."
                                    )}
                                </p>

                                <button
                                    type="button"
                                    className="buttons-ecp buttons-ecp-create"
                                    onClick={reauthenticate}
                                >
                                    {t("Se reconnecter")}
                                </button>
                            </div>
                        )}

                        {history.loading && visibleRequests.length === 0 && (
                            <ScanLoading variant="workflow-table" />
                        )}

                        {!history.loading
                            && !history.error
                            && visibleRequests.length === 0 && (
                                <p>
                                    {t(
                                        "Aucune demande sur cette page."
                                    )}
                                </p>
                            )}

                        {token && (!history.loading || visibleRequests.length > 0) && (
                            <div id="scan-results">
                                <Table
                                    tableData={tableData}
                                    tableColumns={tableColumns}
                                    FileSaverExcelName="scan-requests"
                                    toolbarActions={
                                        <button
                                            type="button"
                                            className="scan-workflow-refresh-button scan-workflow-tooltip"
                                            disabled={history.loading}
                                            aria-busy={history.loading}
                                            aria-label={t(history.loading ? "Actualisation…" : "Actualiser")}
                                            data-tooltip={t(history.loading ? "Actualisation…" : "Actualiser")}
                                            onClick={reload}
                                        >
                                            <RefreshRounded aria-hidden="true" />
                                        </button>
                                    }
                                />
                            </div>
                        )}
                    </section>
                </>
            )}
        </>
    );
}
